import React, { useState, useEffect, version } from 'react';
import axios from '../api/axios'; 
import { 
    FaArrowRight, FaArrowLeft, FaRobot, FaCheckCircle, 
    FaTrash, FaSave, FaSync, FaObjectGroup, FaKeyboard, FaPlusCircle, FaMagic, FaRedo, FaExclamationTriangle
} from 'react-icons/fa';
import MergeTest from './MergeTest'; 
import { SuccessModal, ErrorModal, ConfirmModal, InfoModal, LoadingModal } from './SharedComponents/Modals';

// --- HELPER: FILE TO BASE64 ---
const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const base64String = reader.result.split(',')[1];
            resolve(base64String);
        };
        reader.onerror = (error) => reject(error);
    });
};

const TestManager = ({ auth, testToEdit = null }) => {
    // --- COLORI THEME ---
    const theme = {
        primary: '#0C4A76',    // Blu scuro
        secondary: '#11A0D9',  // Celeste
        accent: '#F2962B',     // Arancione
        bg: '#f8f9fa'
    };

    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [createdTestId, setCreatedTestId] = useState(null);

    // STATO PER MODALITÀ MERGE
    const [isMergeMode, setIsMergeMode] = useState(false);

    // --- GESTIONE MODALI UNIFICATA ---
    const [modalState, setModalState] = useState({
        type: null, // 'success', 'error', 'confirm', 'info', 'loading'
        title: '',
        message: '',
        progress: 0, // Percentuale per il loading
        confirmAction: null
    });

    const closeModal = () => {
        const type = modalState.type;
        setModalState({ type: null, title: '', message: '', progress: 0 });
        // FIX: Il reload avviene solo se l'operazione è un salvataggio completato (success)
        if (type === 'success') {
            window.location.reload();
        }
    };

    const handleMultipleCsv = async () => {
    if (files.length === 0) {
        setModalState({ type: 'error', title: 'Errore', message: 'Carica almeno un CSV.' });
        return;
    }

    if (!csvMultiConfig.category || !csvMultiConfig.num_questions) {
        setModalState({ type: 'error', title: 'Errore', message: 'Compila tutti i campi obbligatori.' });
        return;
    }

    setLoading(true);
    setModalState({
        type: 'loading',
        title: 'Creazione test multipli',
        message: 'Parsing CSV...',
        progress: 10
    });

    try {
        const testsToCreate = [];

        for (let i = 0; i < files.length; i++) {
        const f = files[i];

        const reader = new FileReader();
        reader.readAsText(f);
        await new Promise(r => (reader.onload = r));

        const parsed = parseCSV(reader.result);

        const slicedQuestions = parsed.slice(0, csvMultiConfig.num_questions);

        const testName = csvMultiConfig.auto_test_names
            ? f.name.replace(/\.csv$/i, '')
            : config.name;

        testsToCreate.push({
            ...config,
            name: testName,
            category: csvMultiConfig.category,
            duration: csvMultiConfig.duration,
            questions: slicedQuestions
        });

        setModalState(prev => ({
            ...prev,
            progress: Math.round(((i + 1) / files.length) * 80)
        }));
        }

        // 🔥 Salvataggio N test
        for (const testPayload of testsToCreate) {
        await axios.post('/api_tests_manager.php?action=save_generated_test', {
            ...testPayload,
            difficulty: aiParams.difficulty_level,
            action_type: 'create'
        });
        }

        setModalState({
        type: 'success',
        title: 'Test creati',
        message: `${testsToCreate.length} test creati correttamente.`
        });

    } catch (err) {
        setModalState({ type: 'error', title: 'Errore', message: err.message });
    } finally {
        setLoading(false);
    }
    };



    // Configurazione Iniziale
    const [config, setConfig] = useState({
        name: '', 
        category: '', 
        tenant_id: auth.role === 'superadmin' ? '' : auth.tenant_id, 
        target_group_id: '', 
        duration: 30, 
        pass_threshold: 60,
        valid_from: new Date().toISOString().split('T')[0], 
        valid_to: ''
    });

    const [genMethod, setGenMethod] = useState('ai');
    const [aiParams, setAiParams] = useState({ 
        num_questions: 10, num_answers: 4, difficulty_level: 'Universitario', deep_search: false 
    });
    
    const [csvParams, setCsvParams] = useState({ 
        num_questions: 10, num_answers: 4, difficulty_level: 'Universitario', deep_search: false,
        auto_test_names: true,
        quest_name_prefix: 'Test',
        version_suffix: 'v1'
    });
    const [csvMultiConfig, setCsvMultiConfig] = useState({
        num_questions: 10,
        duration: 40,
        auto_test_names: true,
        category: '',
    });
    
    const [file, setFile] = useState(null);
    const [files, setFiles] = useState([]);
    const [tenants, setTenants] = useState([]); 
    const [groups, setGroups] = useState([]);   
    const [rawCsvOutput, setRawCsvOutput] = useState(""); 
    const [generatedQuestions, setGeneratedQuestions] = useState([]); 

    // --- 1. CARICAMENTO INIZIALE ---
    useEffect(() => {
        const fetchTenants = async () => {
            if (auth.role === 'superadmin') {
                try {
                    const res = await axios.get('/api_tenants.php');
                    if (res.data.success) setTenants(res.data.data || []);
                } catch (err) {
                    setModalState({ type: 'error', title: 'Errore', message: 'Errore caricamento tenants.' });
                }
            }
        };
        fetchTenants();

        if (testToEdit) {
            setConfig({
                id: testToEdit.id,
                name: testToEdit.name,
                category: testToEdit.category || '',
                tenant_id: testToEdit.tenant_id, 
                target_group_id: testToEdit.target_group_id, 
                duration: testToEdit.duration_minutes,
                pass_threshold: testToEdit.pass_threshold,
                valid_from: testToEdit.valid_from || '',
                valid_to: testToEdit.valid_to || ''
            });
        }
    }, [auth.role, testToEdit]);

    // --- 2. CARICAMENTO GRUPPI ---
    useEffect(() => {
        const fetchGroups = async () => {
            if (!config.tenant_id) { setGroups([]); return; }
            if (auth.role !== 'superadmin' && config.tenant_id != auth.tenant_id) return;

            try {
                const res = await axios.get(`/api_tests_manager.php?action=get_params&tenant_id=${config.tenant_id}`);
                setGroups(res.data.success ? res.data.data.groups : []);
            } catch (err) {
                setGroups([]);
            }
        };
        fetchGroups();
    }, [config.tenant_id, auth.role, auth.tenant_id]);

    const handleTenantChange = (e) => {
        setConfig(prev => ({ ...prev, tenant_id: e.target.value, target_group_id: '' }));
    };

    // --- 3. CHIAMATA GEMINI (FIX 429) ---
    const generateQuiz = async (selectedFile) => {
        const API_KEY0 = "AIzaSyDrkNBi10KJ4njezcVDoBHTfMS94Yt-qg0";
        const API_KEY = "AIzaSyD5j44xupvQTJJuzch3x1IGImRCqzqhum4";
        const MODEL_NAME = "gemini-2.5-flash"; 
        const base64Data = await fileToBase64(selectedFile);
        
        let mimeType = selectedFile.type;
        if (selectedFile.name.toLowerCase().endsWith('.pdf')) mimeType = 'application/pdf';
        else if (selectedFile.name.toLowerCase().endsWith('.txt')) mimeType = 'text/plain';

        const deepSearchInstruction = aiParams.deep_search 
            ? "UTILIZZA il tool 'googleSearch' per cercare informazioni recenti..." 
            : "Basati ESCLUSIVAMENTE sul contenuto dei file.";

        const prompt = `
            Sei un esperto Psicometrista e Creatore di Test Universitari.
            Il tuo compito è generare un dataset CSV per un test a risposta multipla basato sul file caricato.

            --- PARAMETRI DEL PROGETTO ---
            - Materia: ${config.category}
            - Livello: ${aiParams.difficulty_level}
            - Obiettivo Domande: ${aiParams.num_questions} (DEVI generarne ESATTAMENTE questo numero)
            - Risposte per domanda: ${aiParams.num_answers} (1 Vera, ${aiParams.num_answers - 1} False)
            - Deep Search Config: ${aiParams.deep_search ? "ATTIVA" : "DISATTIVA"}
            - Istruzioni Extra: ${deepSearchInstruction}

            --- PROCESSO DI LAVORO (Chain of Thought) ---
            !!! REGOLE CRITICHE ANTI-BIAS (Leggere attentamente) !!!
            Non generare subito l'output. Segui questi passaggi mentalmente:

            FASE 1: GENERAZIONE BOZZA (Golden Rule):
            Crea le domande basandoti sul testo, se ${aiParams.deep_search ? "ATTIVA" : "DISATTIVA"} == "ATTIVA" approfondisci con ${deepSearchInstruction}.
            
            1. REGOLA DELLA LUNGHEZZA (Golden Rule): 
               - Genera nella stessa lingua del testo sorgente.
               - La risposta corretta ('V') NON DEVE MAI ESSERE l'unica risposta più lunga (evita bias).
               - I distrattori ('F') devono essere lunghi quanto o PIÙ della risposta corretta.
               - Se la risposta corretta richiede 10 parole, almeno due distrattori devono averne 10-12.
               - Se la risposta corretta ad una domanda è anche la più lunga tra le risposte, almeno il 50% delle altre devono essere di lunghezza simile o più lunghe.
               - Evita risposte 'V' piene di dettagli superflui che le rendono visibilmente diverse.
               - 30% delle domande deve avere risposte "SECCHE" (1-4 parole).
               - Non passare alla fase successiva se questa regola non è rispettata.

            2. REGOLA DELL'OMOGENEITÀ:
               - Tutte le opzioni di una singola domanda devono appartenere alla stessa categoria grammaticale e logica (es. tutte date, tutti nomi propri, tutte frasi ipotetiche).
               - Non mischiare risposte brevi con risposte lunghe nella stessa domanda.

            3. REGOLA DEL CONTENUTO:
               - Evita opzioni come "Tutte le precedenti", "Nessuna delle precedenti", "A e B sono vere".
               - I distrattori devono essere PLAUSIBILI per chi non conosce la materia, non ridicoli.
               - Non usare intestazioni del campo.
            
            4. REGOLE SINTATTICHE:
            - Separatore di campo: punto e virgola (;)
            - Una riga per la Domanda (CON punto e virgola alla fine, SENZA numerazione).
            - Una riga per ogni Risposta vera (Testo;V;Spiegazione obbligatoria;).
            - Una riga per ogni Risposta falsa(Testo;F;) per le risposte false nessuna spiegazione.
            - NESSUNA riga vuota tra domande e risposte dello stesso blocco.

            FASE 2: AUDIT E CORREZIONE (Self-Correction)
            Rileggi le domande generate e verifica:
            1. CONTEGGIO: Sono esattamente ${aiParams.num_questions}? Se sono meno, aggiungine. Se sono troppe, taglia.
            2. STRUTTURA RISPOSTE: Ogni domanda ha esattamente ${aiParams.num_answers} risposte?
            3. FORMATO: Hai usato il punto e virgola (;)? La domanda finisce con ;? Le risposte hanno ;V o ;F?
            4. SINTASSI: Non ci devono essere caratteri speciali che rompono i CSV (come " o a capo non gestiti).

            FASE 3: FORMATO OUTPUT FINALE (CSV STRICT):
            Scrivi SOLO il risultato finale corretto.
            - Il file dovrà contenere esattamente ${aiParams.num_questions}*${aiParams.num_answers} righe.
            - Nessun markdown, nessuna intestazione, solo i dati grezzi.
            - Separatore: punto e virgola (;).
            - Niente Markdown (\`\`\`csv).
            - Niente titoli o introduzioni.
            - Niente numerazione (1. 2. 3.) davanti alle domande.
            - Niente lettere (a) b) c)) davanti alle risposte.
            - Ogni fine domanda ; e capo.
            - Ogni fine rispota ;F; e capo o ;V;Spiegazione; e capo.
            - Nessuna riga vuota.
            - Nelle domande non usare ; all'interno del testo piuttosto una , oppure niente.

            FASE 4: RIPETIZIONE FINO AL COMPLETAMENTO
            Se il ${aiParams.num_questions} non è raggiunto, genera altre domande seguendo esattamente questo pattern.
            Se la golden rule della lunghezza non è rispettata, correggi le risposte per adeguarle.

            STRUTTURA:
            Domanda;
            Risposta;V;[Spiegazione OBBLIGATORIA];
            Risposta;F;[NESSUNA Spiegazione];
            Risposta;F;[NESSUNA Spiegazione];
            
            ESEMPIO POSITIVO (Segui questo pattern):
            Qual è la capitale della Francia?;
            Parigi;V;È la capitale politica ed economica.;
            Lione;F;
            Marsiglia;F;
            Nizza;F;
            In quale anno è caduto il muro di Berlino?;
            1990;F;
            1989;V;Questo evento ha segnato la fine della Guerra Fredda.;
            1988;F;
            1991;F;

            ESEMPI NEGATIVI (Cosa NON fare):
            1. Qual è la capitale?; (NO NUMERI)
            A) Roma (NO LETTERE)
            Qual è la capitale? (NO punto e virgola alla fine)
            Domanda: Qual è la capitale della Francia? (NO ETICHETTE)
            Risposta V: Parigi (NO ETICHETTE)
            Risposta F: Lione (NO ETICHETTE)
            Quanti anni ha la Terra? 200;F;4.5 miliardi di anni;V;la terra è molto vecchia; (DOMANDA NON SEPARATA DALLE RISPOSTE MANCA ; E SONO SULLA STESSA RIGA)
            200;F;4.5 miliardi di anni;V;la terra è molto vecchia; (RISPOSTE SULLA STESSA RIGA)
            Quanti anni ha la Terra?4.5 miliardi di anni;V;la terra è molto vecchia;(DOMANDA E RISPOSTA NELLA STESSA RIGA)
        `;

        const tools = aiParams.deep_search ? [{ google_search: {} }] : [];
        const payload = {
            contents: [{
                parts: [
                    { inline_data: { mime_type: mimeType, data: base64Data } },
                    { text: prompt }
                ]
            }],
            generationConfig: { temperature: 0.5, maxOutputTokens: 8000 }
        };
        if (aiParams.deep_search) payload.tools = tools;

        const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${MODEL_NAME}:generateContent?key=${API_KEY}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        });

        // --- FIX: GESTIONE ERRORE 429 (QUOTA EXCEEDED) ---
        if (response.status === 429) {
            throw new Error("⚠️ Quota IA superata (Errore 429). Hai raggiunto il limite di richieste giornaliere o al minuto. Riprova più tardi.");
        }

        if (!response.ok) throw new Error("Errore chiamata AI");
        const data = await response.json();
        const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
        if (!text) throw new Error("Risposta vuota da Gemini.");

        return text.trim()
            .replace(/^```csv/gi, "")
            .replace(/^```/g, "")
            .replace(/```$/g, "")
            .trim();
    };

    // --- PARSER CSV ---
    const parseCSV = (csvText) => {
        const lines = csvText.split('\n');
        const questions = [];
        let currentQ = null;
        lines.forEach(line => {
            const row = line.trim();
            if (!row) return;
            
            const parts = row.split(';');
            const cleanParts = parts.map(p => p.trim());
            
            const marker = cleanParts[1] ? cleanParts[1].toUpperCase() : '';
            const isAnswer = marker === 'V' || marker === 'F';

            if (!isAnswer) {
                if (currentQ && currentQ.answers.length > 0) questions.push(currentQ);
                let cleanQuestionText = cleanParts[0].replace(/^\d+[\.)]\s*/, '');
                currentQ = { text: cleanQuestionText, answers: [] };
            } else if (currentQ) {
                const cleanAnswerText = cleanParts[0].replace(/^[a-zA-Z][\.)]\s*/, '');
                currentQ.answers.push({ 
                    text: cleanAnswerText, 
                    is_correct: marker === 'V', 
                    explanation: cleanParts[2] || '' 
                });
            }
        });
        
        if (currentQ && currentQ.answers.length > 0) questions.push(currentQ);
        return questions;
    };

    // --- FORMATTER LATO CLIENT ---
    const handleFormatRaw = () => {
        if (!rawCsvOutput) return;

        try {
            let lines = rawCsvOutput.split('\n');
            let formattedLines = [];
            
            lines.forEach(line => {
                let cleanLine = line.trim();
                if (!cleanLine) return;

                if (cleanLine.startsWith('```')) return;

                // Regex migliorata per individuare risposte (più tollerante sugli spazi)
                const isAnswerRegex = /;\s*[vVfF](\s*;|$)/;
                const isAnswer = isAnswerRegex.test(cleanLine);

                if (isAnswer) {
                    // Pulizia Risposta
                    cleanLine = cleanLine.replace(/^[-*•]\s*/, '').replace(/^[a-zA-Z][\.)]\s*/, '');
                    cleanLine = cleanLine.replace(/;\s*v\s*(;|$)/i, ';V;').replace(/;\s*f\s*(;|$)/i, ';F;');
                     if (!cleanLine.endsWith(';')) cleanLine += ';';
                } else {
                    // Pulizia Domanda
                    cleanLine = cleanLine.replace(/^\d+[\.)]\s*/, ''); 
                    if (!cleanLine.endsWith(';')) cleanLine += ';';
                }

                formattedLines.push(cleanLine);
            });

            const newRaw = formattedLines.join('\n');
            
            setRawCsvOutput(newRaw);
            const parsed = parseCSV(newRaw);
            setGeneratedQuestions(parsed);
            
            setModalState({ type: 'info', title: 'Formattazione Completata', message: 'Il testo è stato normalizzato e l\'anteprima aggiornata.' });
        } catch (err) {
            setModalState({ type: 'error', title: 'Errore Formattazione', message: err.message });
        }
    };

    // --- GESTIONE GENERAZIONE E CARICAMENTO ---
    const handleGetRaw = async () => {


        if (!file && genMethod === 'ai') { 
            setModalState({ type: 'error', title: 'File Mancante', message: 'Carica un file PDF o TXT.' }); 
            return; 
        }
        

        if (genMethod === 'multiplecsv' && files.length === 0) {
            setModalState({
                type: 'error',
                title: 'File mancanti',
                message: 'Carica almeno un file CSV.'
            });
            return;
            }

        if (genMethod === 'csv' && !file) {
            setModalState({
                type: 'error',
                title: 'File mancante',
                message: 'Devi caricare un file CSV.'
            });
            return;
            }

        setLoading(true);
        setModalState({ type: 'loading', title: 'Generazione in corso', message: 'Analisi del file...', progress: 10 });

        // Timer references
        let t1, t2;

        try {
            let output = "";
            if (genMethod === 'ai') {
                // Assegna i timer a variabili per poterli cancellare
                t1 = setTimeout(() => setModalState(prev => ({ ...prev, message: 'Invio a Gemini AI...', progress: 30 })), 800);
                t2 = setTimeout(() => setModalState(prev => ({ ...prev, message: 'Elaborazione domande...', progress: 60 })), 2000);
                
                output = await generateQuiz(file);
                
                // Cancella i timer se l'operazione ha successo prima che scattino
                clearTimeout(t1);
                clearTimeout(t2);

                setModalState(prev => ({ ...prev, message: 'Risposta ricevuta!', progress: 85 }));
            } else {
                setModalState(prev => ({ ...prev, message: 'Lettura CSV...', progress: 50 }));
                const reader = new FileReader();
                reader.readAsText(file);
                await new Promise(r => reader.onload = r);
                output = reader.result;
            }

            setModalState(prev => ({ ...prev, message: 'Parsing dati finale...', progress: 95 }));
            setRawCsvOutput(output);
            const parsed = parseCSV(output);
            setGeneratedQuestions(parsed);
            
            setTimeout(() => {
                setModalState({ type: null }); 
                setStep(4);
            }, 600);

        } catch (err) {
            // FIX CRITICO: Cancella i timer anche in caso di errore per evitare sovrascritture del modale
            if (t1) clearTimeout(t1);
            if (t2) clearTimeout(t2);
            setModalState({ type: 'error', title: 'Errore', message: err.message });
        } finally {
            setLoading(false);
        }
    };

    // --- FIX: HANDLE REPARSE AGGIORNATO (Feedback Visivo) ---
    const handleReparse = () => {
        setLoading(true);
        // Introduciamo un piccolo delay per far apparire il loader e dare feedback di azione avvenuta
        setTimeout(() => {
            try {
                const parsed = parseCSV(rawCsvOutput);
                setGeneratedQuestions(parsed);
                // Feedback di successo
                setModalState({ 
                    type: 'info', 
                    title: 'Anteprima Aggiornata', 
                    message: 'La visualizzazione è stata sincronizzata con le modifiche manuali.' 
                });
            } catch (e) { 
                setModalState({ type: 'error', title: 'Errore Parsing', message: e.message });
            } finally {
                setLoading(false);
            }
        }, 300);
    };

    // --- SALVATAGGIO ---
    const initiateSave = () => {
        if (generatedQuestions.length === 0) { 
            setModalState({ type: 'info', title: 'Attenzione', message: 'Nessuna domanda valida da salvare.' }); 
            return; 
        }
        setModalState({
            type: 'confirm',
            title: testToEdit ? 'Conferma Modifica' : 'Conferma Salvataggio',
            message: `Salvare il test "${config.name}" con ${generatedQuestions.length} domande?`,
            confirmAction: executeSave
        });
    };

    const executeSave = async () => {
        setModalState({ type: 'loading', title: 'Salvataggio...', message: 'Scrittura database...', progress: 50 });
        setLoading(true);

        try {
            const payload = {
                ...config,
                difficulty: aiParams.difficulty_level,
                questions: generatedQuestions,
                test_id: testToEdit ? testToEdit.id : null, 
                action_type: testToEdit ? 'update' : 'create'
            };
            const res = await axios.post('/api_tests_manager.php?action=save_generated_test', payload);
            if (res.data.success) {
                setCreatedTestId(res.data.id || (testToEdit ? testToEdit.id : 'N/A'));
                setModalState(prev => ({ ...prev, progress: 100 }));
                setTimeout(() => {
                    setModalState({ 
                        type: 'success', 
                        title: 'Test Salvato!', 
                        message: 'Salvataggio completato.' 
                    });
                }, 500);
            } else { 
                throw new Error(res.data.message); 
            }
        } catch (err) {
            setModalState({ type: 'error', title: 'Errore Salvataggio', message: err.message });
        } finally { 
            setLoading(false); 
        }
    };

    const removeQuestion = (idx) => {
        setGeneratedQuestions(generatedQuestions.filter((_, i) => i !== idx));
    };

    // --- RENDER ---
    if (isMergeMode) {
        return (
            <div className="p-4 fade-in">
                <MergeTest 
                    auth={auth} 
                    onCancel={() => setIsMergeMode(false)} 
                    onSuccess={() => { setIsMergeMode(false); window.location.reload(); }}
                />
            </div>
        );
    }

    return (
        <div className="p-4 fade-in position-relative">
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h4 className="fw-bold m-0" style={{color: theme.primary}}>
                    <FaRobot className="me-2"/>
                    {testToEdit ? `Modifica: ${testToEdit.name}` : 'Nuovo Test'}
                </h4>
            </div>
            
            {/* --- MODALI --- */}
            <SuccessModal show={modalState.type === 'success'} onClose={closeModal} onConfirm={closeModal} title={modalState.title} message={modalState.message} confirmText="OK"/>
            <ErrorModal show={modalState.type === 'error'} onClose={closeModal} title={modalState.title} message={modalState.message}/>
            <InfoModal show={modalState.type === 'info'} onClose={closeModal} title={modalState.title} message={modalState.message}/>
            <ConfirmModal show={modalState.type === 'confirm'} onClose={closeModal} onConfirm={modalState.confirmAction} title={modalState.title} message={modalState.message} confirmText="Salva" confirmColor="btn-success"/>
            <LoadingModal show={modalState.type === 'loading'} title={modalState.title} message={modalState.message} progress={modalState.progress}/>

            <div className="progress mb-4" style={{height:'6px'}}>
                <div className="progress-bar" style={{width: `${step*25}%`, backgroundColor: theme.secondary}}></div>
            </div>
            
            {/* STEP 1 */}
            {step === 1 && (
                <div className="card border-0 shadow-sm p-4" style={{backgroundColor: '#fff'}}>
                    <h5 className="mb-3 fw-bold" style={{color: theme.primary}}>Step 1: Configurazione Base</h5>
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="small fw-bold text-muted">Nome Test *</label>
                            <input className="form-control" value={config.name} onChange={e=>setConfig({...config, name:e.target.value})}/>
                        </div>
                        <div className="col-md-6">
                            <label className="small fw-bold text-muted">Materia *</label>
                            <input className="form-control" value={config.category} onChange={e=>setConfig({...config, category:e.target.value})}/>
                        </div>
                        {auth.role === 'superadmin' ? (
                            <div className="col-md-6">
                                <label className="small fw-bold text-danger">Azienda (Tenant) *</label>
                                <select className="form-select" value={config.tenant_id} onChange={handleTenantChange}>
                                    <option value="">-- Seleziona --</option>
                                    {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                            </div>
                        ) : null}
                        <div className="col-md-6">
                            <label className="small fw-bold text-muted">Gruppo *</label>
                            <select className="form-select" value={config.target_group_id} onChange={e=>setConfig({...config, target_group_id:e.target.value})} disabled={!config.tenant_id}>
                                <option value="">-- Seleziona --</option>
                                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="d-flex justify-content-end mt-4">
                        <button className="btn text-white fw-bold px-4" style={{backgroundColor: theme.primary}} 
                            onClick={() => {
                                if(!config.name || !config.category || !config.tenant_id || !config.target_group_id) { 
                                    setModalState({type:'error', title:'Dati Mancanti', message:'Compila tutti i campi obbligatori.'}); return; 
                                }
                                setStep(2);
                            }}>
                            Avanti <FaArrowRight className="ms-2"/>
                        </button>
                    </div>
                </div>
            )}

            {/* STEP 2 */}
            {step === 2 && (
                <div className="text-center py-5">
                    <div className="card border-0 shadow-sm p-5 d-inline-block" style={{backgroundColor: '#eef6fc'}}>
                        <h5 className="fw-bold" style={{color: theme.primary}}>Risposta Multipla</h5>
                        <FaCheckCircle className="fs-1 mt-3" style={{color: theme.secondary}}/>
                    </div>
                    <div className="mt-5">
                        <button className="btn btn-outline-secondary me-3 px-4" onClick={()=>setStep(1)}><FaArrowLeft/> Indietro</button>
                        <button className="btn text-white fw-bold px-4" style={{backgroundColor: theme.primary}} onClick={()=>setStep(3)}>Avanti <FaArrowRight/></button>
                    </div>
                </div>
            )}

            {/* STEP 3 */}
            {step === 3 && (
                <div className="card border-0 shadow-sm p-4">
                    <h5 className="mb-4 fw-bold" style={{color: theme.primary}}>Step 3: Fonte e Metodo</h5>
                    <div className="d-flex gap-3 mb-4 border-bottom pb-3">
                        <button className={`btn ${genMethod==='ai' ? 'text-white' : 'btn-outline-secondary'}`} style={genMethod==='ai' ? {backgroundColor: theme.secondary, border:'none'} : {}} onClick={()=>setGenMethod('ai')}>
                            <FaRobot className="me-2"/> Generazione AI
                        </button>
                        <button className={`btn ${genMethod==='csv' ? 'text-white' : 'btn-outline-secondary'}`} style={genMethod==='csv' ? {backgroundColor: theme.secondary, border:'none'} : {}} onClick={()=>setGenMethod('csv')}>
                            <FaKeyboard className="me-2"/> Manuale (CSV)
                        </button>
                        <button className={`btn ${genMethod==='multiplecsv' ? 'text-white' : 'btn-outline-secondary'}`} style={genMethod==='multiplecsv' ? {backgroundColor: theme.secondary, border:'none'} : {}} onClick={()=>setGenMethod('multiplecsv')}>
                            <FaKeyboard className="me-2"/> Creazione test multipli (CSV)
                        </button>
                        <button className="btn btn-outline-warning fw-bold" style={{color: theme.accent, borderColor: theme.accent}} onClick={() => setIsMergeMode(true)}>
                            <FaObjectGroup className="me-2"/> Merge Test
                        </button>
                    </div>

                    {genMethod === 'ai' && (
                        <div className="bg-light p-3 rounded border">
                            <div className="row g-3">
                                <div className="col-12">
                                    <label className="fw-bold text-muted">Carica Documento (PDF/TXT)</label>
                                    <input type="file" className="form-control" onChange={e=>{
                                        setFile(e.target.files[0])
                                        setFiles([]);
                                    }} accept=".pdf,.txt" />
                                </div>
                                <div className="col-md-4"><label className="small fw-bold">Domande</label><input type="number" className="form-control" value={aiParams.num_questions} onChange={e=>setAiParams({...aiParams, num_questions:e.target.value})}/></div>
                                <div className="col-md-4"><label className="small fw-bold">Opzioni</label><input type="number" className="form-control" value={aiParams.num_answers} onChange={e=>setAiParams({...aiParams, num_answers:e.target.value})}/></div>
                                <div className="col-md-4"><label className="small fw-bold">Livello</label><select className="form-select" value={aiParams.difficulty_level} onChange={e=>setAiParams({...aiParams, difficulty_level:e.target.value})}><option value="Normale">Normale</option><option value="Universitario">Universitario</option></select></div>
                                <div className="col-12"><div className="form-check form-switch"><input className="form-check-input" type="checkbox" id="ds" checked={aiParams.deep_search} onChange={e=>setAiParams({...aiParams, deep_search:e.target.checked})}/><label className="form-check-label fw-bold" htmlFor="ds">Deep Search (Approfondimento Online)</label></div></div>
                            </div>
                        </div>
                    )}
                    
                    {genMethod === 'csv' && (
                        <div className="bg-light p-3 rounded border">
                            <label className="fw-bold text-muted">Carica CSV Formattato</label>
                            <input type="file" className="form-control" onChange={e=>{
                                setFile(e.target.files[0])
                                setFiles(null);
                            }} accept=".csv"/>
                            <small className="text-muted d-block mt-1 fst-italic">Formato: Domanda;V/F;[Spiegazione]</small>
                        </div>
                    )}

                    {genMethod === 'multiplecsv' && (
                    <div className="bg-light p-3 rounded border">
                        <div className="row g-3">

                        {/* Upload */}
                        <div className="col-12">
                            <label className="fw-bold text-muted">Carica CSV Multipli *</label>
                            <input
                            type="file"
                            className="form-control"
                            multiple
                            accept=".csv"
                            onChange={e => {
                                setFiles(Array.from(e.target.files));
                                setFile(null);
                            }}
                            />
                        </div>

                        {/* Lista file */}
                        {files.length > 0 && (
                            <div className="col-12">
                            <ul className="list-group">
                                {files.map((f, i) => (
                                <li key={i} className="list-group-item">
                                    {f.name}
                                </li>
                                ))}
                            </ul>
                            </div>
                        )}

                        {/* Config */}
                        <div className="col-md-4">
                            <label className="small fw-bold">Numero domande *</label>
                            <input
                            type="number"
                            className="form-control"
                            value={csvMultiConfig.num_questions}
                            min={1}
                            onChange={e =>
                                setCsvMultiConfig({ ...csvMultiConfig, num_questions: Number(e.target.value) })
                            }
                            />
                        </div>

                        <div className="col-md-4">
                            <label className="small fw-bold">Durata test (min)</label>
                            <input
                            type="number"
                            className="form-control"
                            value={csvMultiConfig.duration}
                            onChange={e =>
                                setCsvMultiConfig({ ...csvMultiConfig, duration: Number(e.target.value) })
                            }
                            />
                        </div>

                        <div className="col-md-4">
                            <label className="small fw-bold">Categoria *</label>
                            <input
                            className="form-control"
                            value={csvMultiConfig.category}
                            onChange={e =>
                                setCsvMultiConfig({ ...csvMultiConfig, category: e.target.value })
                            }
                            />
                        </div>

                        <div className="col-12">
                            <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={csvMultiConfig.auto_test_names}
                                onChange={e =>
                                setCsvMultiConfig({ ...csvMultiConfig, auto_test_names: e.target.checked })
                                }
                            />
                            <label className="form-check-label fw-bold">
                                Genera automaticamente i nomi test (dal nome del CSV)
                            </label>
                            </div>
                        </div>

                        </div>
                    </div>
                    )}

                    <div className="mt-4 d-flex justify-content-between">
                        <button className="btn btn-outline-secondary px-4" onClick={()=>setStep(2)}><FaArrowLeft/> Indietro</button>
                        <button className="btn text-white fw-bold px-4" style={{backgroundColor: theme.accent, border:'none'}} disabled={loading} onClick={() => {
                            if (genMethod === 'multiplecsv') {
                                handleMultipleCsv();
                            } else {
                                handleGetRaw();
                            }
                        }}>
                            {loading ? 'Elaborazione...' : 'GENERA ANTEPRIMA'}
                        </button>
                    </div>
                </div>
            )}

            {/* STEP 4: PREVIEW */}
            {step === 4 && (
                <div className="row h-100">
                    <div className="col-md-5 mb-3">
                        <div className="card shadow-sm h-100">
                            <div className="card-header text-white d-flex justify-content-between align-items-center py-2" style={{backgroundColor: '#333'}}>
                                <span className="small fw-bold">Output RAW</span>
                                {/* FIX: Pulsanti più chiari e parlanti */}
                                <div className="d-flex gap-2">
                                    <button className="btn btn-sm btn-info text-white fw-bold" onClick={handleFormatRaw} title="Pulisci e Formatta automaticamente">
                                        <FaMagic className="me-1"/> Formatta
                                    </button>
                                    <button className="btn btn-sm btn-warning text-dark fw-bold" onClick={handleReparse} title="Aggiorna l'anteprima dalle modifiche manuali">
                                        <FaSync className="me-1"/> Aggiorna Vista
                                    </button>
                                    <button className="btn btn-sm btn-danger text-white fw-bold" onClick={handleGetRaw} title="Rigenera completamente tutte le domande con l'AI">
                                        <FaRedo className="me-1"/> Rigenera Tutto
                                    </button>
                                </div>
                            </div>
                            <textarea className="form-control border-0 h-100 p-2" style={{minHeight:'400px', fontFamily:'monospace', fontSize:'0.85rem', resize:'none'}} value={rawCsvOutput} onChange={(e) => setRawCsvOutput(e.target.value)}></textarea>
                        </div>
                    </div>
                    <div className="col-md-7 mb-3">
                        <div className="card shadow-sm h-100">
                            <div className="card-header text-white d-flex justify-content-between align-items-center py-2" style={{backgroundColor: theme.secondary}}>
                                <span className="small fw-bold">Anteprima ({generatedQuestions.length})</span>
                                <button className="btn btn-sm btn-light fw-bold" style={{color: theme.primary}} onClick={initiateSave} disabled={loading}><FaSave className="me-1"/> SALVA TEST</button>
                            </div>
                            <div className="card-body bg-light p-2" style={{maxHeight:'400px', overflowY:'auto'}}>
                                {generatedQuestions.map((q, i) => (
                                    <div key={i} className="card mb-2 border-0 shadow-sm">
                                        <div className="card-header bg-white py-1 d-flex justify-content-between">
                                            <span className="fw-bold text-primary">#{i+1} {q.text}</span>
                                            <button className="btn btn-sm text-danger border-0" onClick={()=>removeQuestion(i)}><FaTrash/></button>
                                        </div>
                                        <div className="card-body p-2">
                                            {q.answers.map((a, j) => (
                                                <div key={j} className={`p-1 ${a.is_correct ? 'text-success fw-bold' : 'text-muted'}`}>
                                                    {a.is_correct ? <FaCheckCircle className="me-1"/> : <span className="me-3">○</span>} 
                                                    {a.text}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="col-12 mt-2"><button className="btn btn-outline-secondary" onClick={()=>setStep(3)}>Indietro</button></div>
                </div>
            )}
        </div>
    );
};

export default TestManager;